import { parseUTCDate, isDateInRange } from './dateToolkit';

/**
 * Filters an array of items based on one or more date fields falling within a specified UTC date range.
 *
 * @param {Array<Object>} items - The array of items to filter.
 * @param {string|string[]} dateFields - The key(s) in each item that contain the date string(s).
 * @param {dayjs.Dayjs} startUTC - The start date (inclusive) in UTC as a Day.js object.
 * @param {dayjs.Dayjs} endUTC - The end date (inclusive) in UTC as a Day.js object.
 * @returns {Array<Object>} - The filtered array of items where all dateFields are within the date range.
 *
 * @throws {TypeError} If any of the parameters are of incorrect type.
 */
export const filterByDateRange = (items, dateFields, startUTC, endUTC) => {
  if (!Array.isArray(items)) {
    throw new TypeError('The "items" parameter must be an array.');
  }
  if (typeof dateFields !== 'string' && !Array.isArray(dateFields)) {
    throw new TypeError(
      'The "dateFields" parameter must be a string or an array of strings.'
    );
  }
  if (!startUTC || typeof startUTC.isSameOrAfter !== 'function') {
    throw new TypeError('The "startUTC" parameter must be a Day.js object.');
  }
  if (!endUTC || typeof endUTC.isSameOrBefore !== 'function') {
    throw new TypeError('The "endUTC" parameter must be a Day.js object.');
  }

  const dateFieldsArray = Array.isArray(dateFields) ? dateFields : [dateFields];

  return items.filter((item) => {
    return dateFieldsArray.every((dateField) => {
      const dateValue = item[dateField];
      if (!dateValue) {
        return false;
      }
      const date = parseUTCDate(dateValue);
      if (!date.isValid()) {
        return false;
      }
      return isDateInRange(date, startUTC, endUTC);
    });
  });
};
